import React, { useState, useEffect, Suspense } from 'react';
import { auth } from './firebase';
import db from './services/db';
import { onAuthStateChanged, signOut, updatePassword } from 'firebase/auth';
import Login from './Login';
import AppLayout from './components/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { OrderModal } from './Products';
import { useSiteSettings } from './hooks/useSiteSettings';
import { SettingsProvider } from './contexts/SettingsContext';
import { startSession, endSession, attachUnloadHandler } from './lib/activityTracker';
import InactivityLogout from './components/InactivityLogout';
import './App.css';
import { STAFF_LOGO, STAFF_NAME } from './utils/brand';
import { SecureImg, SecureLink } from './components/SecureFile';

// Code-split: each route component downloads on first use.
// OrderModal is kept as a static named import above because React.lazy only
// supports default exports, and OrderModal is opened inline from the public
// site / category page handlers (not via a route gate).
const AdminPanel             = React.lazy(() => import('./AdminPanel'));
const AgentDashboard         = React.lazy(() => import('./AgentDashboard'));
const RecoveryDashboard      = React.lazy(() => import('./RecoveryDashboard'));
const CityManagerDashboard   = React.lazy(() => import('./components/CityManagerDashboard'));
const BranchManagerDashboard = React.lazy(() => import('./components/BranchManagerDashboard'));
const FieldCoordinatorDashboard = React.lazy(() => import('./components/FieldCoordinatorDashboard'));
const LegalOfficerDashboard     = React.lazy(() => import('./components/LegalOfficerDashboard'));
const SalesmanDashboard         = React.lazy(() => import('./components/SalesmanDashboard'));
const CustomerPortal         = React.lazy(() => import('./components/CustomerPortal'));
const Products               = React.lazy(() => import('./Products'));
const AlSamiProductPage      = React.lazy(() => import('./AlSamiProductPage'));
const TrackOrder             = React.lazy(() => import('./TrackOrder'));
const CategoryPage           = React.lazy(() => import('./CategoryPage'));

// Non-admin dashboards (Inquiry/Recovery/CityManager/BranchManager) never mount
// AdminPanel, so they mount the shared <InactivityLogout> directly (same 10-min
// modal-based auto-logout the admin panel uses — one consistent system for all
// roles). Replaces the old 30-min alert-based useSessionTimeout.

// ── helpers ───────────────────────────────────────────────────────────────────

function applyMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function applyMetaProp(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

// ── Banner Carousel ───────────────────────────────────────────────────────────

function BannerCarousel({ banners }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (!banners.length) return null;
  const b = banners[idx];

  return (
    <div className="banner-carousel">
      {b.imageUrl && <SecureImg src={b.imageUrl} alt={b.title || 'Banner'} className="bc-img" loading="eager" fetchPriority="high" />}
      <div className="bc-overlay">
        <div className="bc-content">
          {b.title    && <h2 className="bc-title">{b.title}</h2>}
          {b.subtitle && <p  className="bc-sub">{b.subtitle}</p>}
          {b.btnText  && (
            <a
              href={b.linkUrl || '#'}
              className="bc-btn"
              onClick={e => {
                if (!b.linkUrl || b.linkUrl.startsWith('#')) {
                  e.preventDefault();
                  if (b.linkUrl) document.querySelector(b.linkUrl)?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              {b.btnText}
            </a>
          )}
        </div>
      </div>
      {banners.length > 1 && (
        <>
          <button className="bc-arrow bc-arrow--l" aria-label="Previous banner" onClick={() => setIdx(i => (i - 1 + banners.length) % banners.length)}>‹</button>
          <button className="bc-arrow bc-arrow--r" aria-label="Next banner" onClick={() => setIdx(i => (i + 1) % banners.length)}>›</button>
          <div className="bc-dots">
            {banners.map((_, i) => (
              <button key={i} className={`bc-dot${i === idx ? ' on' : ''}`} onClick={() => setIdx(i)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── WhatsApp FAB ──────────────────────────────────────────────────────────────

function WaFab({ settings }) {
  if (!settings?.enabled || !settings?.number) return null;
  const url = `https://wa.me/${settings.number}?text=${encodeURIComponent(settings.message || '')}`;
  return (
    <SecureLink href={url} target="_blank" rel="noreferrer"
      className={`wa-fab wa-fab--${settings.position || 'right'}`}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span>WhatsApp</span>
    </SecureLink>
  );
}

// ── Site Popup ────────────────────────────────────────────────────────────────

function SitePopup({ settings, onClose }) {
  if (!settings?.enabled || (!settings?.title && !settings?.message)) return null;
  return (
    <div className="site-popup-overlay" onClick={onClose}>
      <div className="site-popup" onClick={e => e.stopPropagation()}>
        <button className="site-popup-close" onClick={onClose}>✕</button>
        {settings.imageUrl && (
          <SecureImg src={settings.imageUrl} alt="" className="site-popup-img" />
        )}
        {settings.title   && <h3 className="site-popup-title">{settings.title}</h3>}
        {settings.message && <p  className="site-popup-msg">{settings.message}</p>}
        <button className="site-popup-btn" onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

// ── Contact Us page ───────────────────────────────────────────────────────────

function ContactUs({ general, whatsapp }) {
  const g = general || {};
  const waNumber = g.whatsapp || whatsapp?.number || '923178600078';
  return (
    <div className="contact-page">
      <div className="contact-hero">
        <h2>Contact Us</h2>
        <p>We're here to help. Reach out anytime.</p>
      </div>
      <div className="contact-grid">
        <div className="contact-card">
          <div className="contact-icon">📍</div>
          <h3>Address</h3>
          <p>{g.address || 'Quetta, Balochistan, Pakistan'}</p>
        </div>
        <div className="contact-card">
          <div className="contact-icon">📞</div>
          <h3>Phone</h3>
          <p>{g.phone || '0317-8600078'}</p>
        </div>
        <div className="contact-card">
          <div className="contact-icon">🕒</div>
          <h3>Business Hours</h3>
          {g.hours ? (
            <p style={{ whiteSpace: 'pre-line' }}>{g.hours}</p>
          ) : (
            <>
              <p>Monday – Saturday</p>
              <p>9:00 AM – 8:00 PM</p>
              <p style={{ marginTop: 6 }}>Sunday</p>
              <p>11:00 AM – 6:00 PM</p>
            </>
          )}
        </div>
        <div className="contact-card">
          <div className="contact-icon">💬</div>
          <h3>WhatsApp</h3>
          <p>Chat with us directly for quick help</p>
          <SecureLink
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noreferrer"
            className="contact-wa-btn"
          >
            Open WhatsApp
          </SecureLink>
        </div>
      </div>
    </div>
  );
}

// ── Change-password gate (Phase 7 F4) ─────────────────────────────────────────
function ChangePasswordRequired({ user, employeeId, onDone, onLogout }) {
  const [pw, setPw]     = useState('');
  const [pw2, setPw2]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2)    { setError('Passwords do not match.'); return; }
    setSaving(true);
    setError('');
    try {
      await updatePassword(user, pw);
      await db.updateDoc('employees', employeeId, { mustChangePassword: false, passwordChangedAt: Date.now() });
      onDone();
    } catch (err) {
      setError(err?.message || 'Failed to change password. You may need to sign in again.');
      setSaving(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <SecureImg src={STAFF_LOGO} alt={STAFF_NAME} className="login-logo login-logo-mark" />
        <h2>Change Password</h2>
        <p>You must set a new password before continuing.</p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={submit}>
          <div className="input-group">
            <label>New Password</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Min. 8 characters" minLength={8} required />
          </div>
          <div className="input-group">
            <label>Confirm Password</label>
            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Repeat password" minLength={8} required />
          </div>
          <button type="submit" disabled={saving || pw.length < 8 || pw !== pw2}>
            {saving ? 'Saving…' : 'Set New Password'}
          </button>
          <button type="button" onClick={onLogout} style={{ marginTop: 8, background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb' }}>
            Sign Out
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Suspense fallback for lazy-loaded route chunks ────────────────────────────

const PageLoader = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
  }}>
    <div style={{
      width: 24,
      height: 24,
      border: '2px solid #e5e7eb',
      borderTopColor: '#1D1D1F',
      borderRadius: '50%',
      animation: 'spin 0.6s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── App ───────────────────────────────────────────────────────────────────────

function App() {
  const [user,           setUser]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [activePage,     setActivePage]     = useState('home');
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories,     setCategories]     = useState([]);
  const [userRole,       setUserRole]       = useState(null);
  const [agentEmployee,  setAgentEmployee]  = useState(null);
  const [isSuperAdmin,   setIsSuperAdmin]   = useState(false);
  const [employeePerms,  setEmployeePerms]  = useState(null);
  const [employeeName,   setEmployeeName]   = useState('');
  const [mustChangePw,   setMustChangePw]   = useState(false);
  // Per-user access date window (Fix 6). Holds the resolved employee's optional
  // accessFromDate / accessToDate ('YYYY-MM-DD' strings) so the admin route can
  // gate the dashboard before/after the allowed window. null = no employee / no limits.
  const [accessWindow,   setAccessWindow]   = useState(null);
  // Gates the admin-route render between successful sign-in and the moment
  // /employees/{uid}/permissions is read & applied — prevents a flash where
  // the panel briefly renders all tabs before restrictions kick in.
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [showPopup,      setShowPopup]      = useState(false);
  const [homeFilter,     setHomeFilter]     = useState('cash');
  const [searchOpen,     setSearchOpen]     = useState(false);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [catOrder,       setCatOrder]       = useState(null);

  const isAdminRoute  = window.location.pathname === '/admin';
  const isPortalRoute = window.location.pathname === '/portal';
  const isProductPage = window.location.pathname.startsWith('/products/');
  const productSlug = isProductPage ? window.location.pathname.replace('/products/', '') : null;

  // Load settings via hook (skip on admin routes to avoid unnecessary subscription)
  const settings = useSiteSettings('alsami', isAdminRoute);
  const { general, banners, announcement: ann, whatsapp, sale, social, seo, popup } = settings;

  // Load categories
  useEffect(() => {
    if (!isAdminRoute) {
      return db.subscribe('categories', rawData => {
        const data = rawData || {};
        setCategories(Object.entries(data).map(([id, v]) => ({ id, ...v })).filter(Boolean));
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply SEO from settings
  useEffect(() => {
    if (!seo || Object.keys(seo).length === 0) return;
    if (seo.metaTitle)       document.title = seo.metaTitle;
    if (seo.metaDescription) applyMeta('description', seo.metaDescription);
    if (seo.keywords)        applyMeta('keywords',    seo.keywords);
    if (seo.ogImageUrl)      applyMetaProp('og:image', seo.ogImageUrl);

    if (seo.gaId && !document.getElementById('ga-script')) {
      const s1 = document.createElement('script');
      s1.id = 'ga-script'; s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${seo.gaId}`;
      document.head.appendChild(s1);
      const s2 = document.createElement('script');
      s2.id = 'ga-init';
      s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${seo.gaId}');`;
      document.head.appendChild(s2);
    }

    if (seo.fbPixelId && !document.getElementById('fb-pixel')) {
      const s = document.createElement('script');
      s.id = 'fb-pixel';
      s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${seo.fbPixelId}');fbq('track','PageView');`;
      document.head.appendChild(s);
    }
  }, [seo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger popup
  useEffect(() => {
    if (!popup?.enabled || (!popup?.title && !popup?.message)) return;
    const KEY = 'alsami_popup_shown';
    if (popup.frequency === 'session' && sessionStorage.getItem(KEY)) return;
    const t = setTimeout(() => {
      setShowPopup(true);
      if (popup.frequency === 'session') sessionStorage.setItem(KEY, '1');
    }, 1800);
    return () => clearTimeout(t);
  }, [popup]);

  // Track visits
  useEffect(() => {
    if (!isAdminRoute && !sessionStorage.getItem('visit_tracked')) {
      const device = /Mobi|Android|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
      const date   = new Date().toISOString().split('T')[0];
      db.pushSubdoc('analytics', 'visits', 'entries', { timestamp: Date.now(), device, date });
      sessionStorage.setItem('visit_tracked', '1');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Best-effort session close on tab close / refresh.
  useEffect(() => attachUnloadHandler(), []);

  // Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Block the admin-route render until permissions are fully resolved.
        setPermissionsLoading(true);
        // Open a session record for performance tracking. Fire-and-forget.
        startSession(currentUser.uid);
        // ── DEBUG ──────────────────────────────────────────────────────────
        if (process.env.NODE_ENV !== 'production') console.log('[Auth] Sign-in detected', {
          uid: currentUser.uid,
          email: currentUser.email,
        });

        await currentUser.getIdToken();

        // The revoked-login check and the two record reads used to run one after another — three
        // round trips before anything showed. They run together now (owner, 19 Oct 2026); nothing
        // is shown until all three are back, and a revoked login is still signed out first.
        const [revoked, adminRead, meRead] = await Promise.all([
          currentUser.getIdToken(true).then(() => null, (err) => err), // force refresh — fails if tokens revoked
          db.getDoc('admins', currentUser.uid).then((v) => ({ v }), (e) => ({ e })),
          db.getDoc('employees', currentUser.uid).then((v) => ({ v }), (e) => ({ e })),
        ]);
        if (revoked && (revoked.code === 'auth/user-token-expired' || revoked.code === 'auth/id-token-expired' || revoked.code === 'auth/user-disabled')) {
          await signOut(auth);
          return;
        }

        // 1) Super-admin check.
        let superAdmin = false;
        try {
          if (adminRead.e) throw adminRead.e;
          const adminSnap = adminRead.v;
          superAdmin = !!adminSnap;
          if (process.env.NODE_ENV !== 'production') console.log('[Auth] /admins/' + currentUser.uid + ' exists?', superAdmin);
        } catch (e) {
          console.warn('[Auth] /admins read failed (expected for non-admins):', e?.code || e);
        }
        setIsSuperAdmin(superAdmin);

        // 2) Find this user's employee record.
        //    First, try the per-uid path — DB rules permit any signed-in user to read
        //    employees/{their-own-uid}. This is the only path that works for non-admin
        //    employees (the parent /employees node is admin-only).
        let empId = null;
        let empData = null;
        try {
          if (meRead.e) throw meRead.e;
          const meData = meRead.v;
          if (process.env.NODE_ENV !== 'production') console.log('[Auth] per-uid read employees/' + currentUser.uid, {
            exists: !!meData,
            value:  meData,
          });
          if (meData) {
            empId   = currentUser.uid;
            empData = meData;
          }
        } catch (e) {
          console.warn('[Auth] per-uid read failed:', e?.code || e);
        }

        // 2b) Fallback for super-admins only — scan the full collection for legacy
        //     pushKey-keyed records (the ones created before AddEmployee saved by uid).
        if (!empData && superAdmin) {
          try {
            const all = (await db.getAll('employees')) || {};
            const entry = Object.entries(all).find(([, v]) => v && v.uid === currentUser.uid);
            if (process.env.NODE_ENV !== 'production') console.log('[Auth] super-admin fallback scan: legacy match?', !!entry);
            if (entry) { empId = entry[0]; empData = entry[1]; }
          } catch (e) {
            console.warn('[Auth] full /employees read failed:', e?.code || e);
          }
        }

        if (process.env.NODE_ENV !== 'production') console.log('[Auth] resolved empData', { empId, empData });
        if (process.env.NODE_ENV !== 'production') console.log('[Auth] empData.permissions =', empData?.permissions);

        // 3) Apply any pending password change queued by an admin.
        if (empData && empData._pendingPassword) {
          try {
            await updatePassword(currentUser, empData._pendingPassword);
            await db.updateDoc('employees', empId, { _pendingPassword: null });
          } catch (err) {
            // Phase 4 — surface the failure to the console at minimum, and
            // clear the queued password so we don't retry indefinitely on
            // every login. The most common cause is `requires-recent-login`;
            // a re-queue from the admin (after the user logs in again) will
            // succeed.
            console.error('[Auth] Password update failed:', err?.code || err?.message || err);
            if (err?.code === 'auth/requires-recent-login') {
              console.warn('[Auth] Skipping password update — requires recent login; admin can re-queue if needed.');
            }
            await db.updateDoc('employees', empId, { _pendingPassword: null }).catch(() => {});
          }
        }

        // Phase 7 F4: must-change-password gate
        setMustChangePw(!!(empData && empData.mustChangePassword));

        // Fix 6: capture the optional per-user access date window so the admin
        // route can block dashboards before/after the allowed period.
        setAccessWindow(empData
          ? { from: empData.accessFromDate || '', to: empData.accessToDate || '' }
          : null);

        // 4) Route by role + permissions.
        // Phase 4 Fix 3 — case-insensitive role match so "inquiry agent",
        // "Inquiry Agent", "InquiryAgent" etc. all dispatch correctly.
        const role = (empData?.role || '').trim().toLowerCase();
        let resolvedPerms = null;
        if (empData && role === 'inquiry agent') {
          setUserRole('agent');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || empData.fullName || '');
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else if (empData && role === 'recovery officer') {
          setUserRole('recovery');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || empData.fullName || '');
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else if (empData && role === 'city manager') {
          setUserRole('cityManager');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || empData.fullName || '');
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else if (empData && role === 'branch manager') {
          setUserRole('branchManager');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || '');
          setEmployeePerms(null);
        } else if (empData && role === 'field coordinator') {
          setUserRole('fieldCoordinator');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || empData.fullName || '');
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else if (empData && role === 'salesman') {
          setUserRole('salesman');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || empData.fullName || '');
          // null, like every other dedicated-dashboard role: hasPermission()
          // treats null as "grant nothing", so AppSidebar renders only the
          // Salesman dashboard entry and no other module is reachable.
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else if (empData && role === 'legal officer') {
          setUserRole('legalOfficer');
          setAgentEmployee({ id: empId, ...empData });
          setEmployeeName(empData.name || empData.fullName || '');
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else if (empData) {
          setUserRole('admin');
          setAgentEmployee(null);
          setEmployeeName(empData.name || empData.fullName || '');
          // Super-admin → unrestricted; otherwise enforce stored perms (missing → empty = Access Denied)
          resolvedPerms = superAdmin ? null : (empData.permissions || {});
          setEmployeePerms(resolvedPerms);
        } else if (superAdmin) {
          // Admin without an employee record (e.g. only listed in /admins) → unrestricted.
          setUserRole('admin');
          setAgentEmployee(null);
          setEmployeeName('');
          resolvedPerms = null;
          setEmployeePerms(resolvedPerms);
        } else {
          // Logged-in user with no per-uid /employees record and not in /admins.
          // This covers legacy pushKey-keyed employees we can't read under current rules.
          // Lock them out by returning empty perms → AdminPanel renders Access Denied.
          setUserRole('admin');
          setAgentEmployee(null);
          setEmployeeName('');
          resolvedPerms = {};
          setEmployeePerms(resolvedPerms);
        }

        if (process.env.NODE_ENV !== 'production') console.log('[Auth] FINAL state →', {
          isSuperAdmin: superAdmin,
          role: empData?.role || (superAdmin ? '(super-admin, no employee record)' : '(no record)'),
          employeePerms: resolvedPerms,
          interpretation:
            resolvedPerms === null ? 'UNRESTRICTED (all tabs visible)' :
            Object.keys(resolvedPerms).length === 0 ? 'ACCESS DENIED (no perms granted)' :
            'RESTRICTED (filtered nav)',
        });

        // Permissions resolved — release the gate AFTER all setters so the
        // first render of the admin route already has the correct prop.
        setPermissionsLoading(false);
      } else {
        setUserRole(null);
        setAgentEmployee(null);
        setIsSuperAdmin(false);
        setEmployeePerms(null);
        setEmployeeName('');
        setMustChangePw(false);
        setAccessWindow(null);
        setPermissionsLoading(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try { await endSession(); } catch (_) {}
    await signOut(auth);
    window.location.href = '/admin';
  };
  const goTo         = (page) => { setActivePage(page); setDrawerOpen(false); setActiveCategory(null); if (page !== 'home') { setSearchOpen(false); setSearchQuery(''); } };
  const goToCategory = (cat)  => { setActiveCategory(cat); setActivePage('category'); setDrawerOpen(false); };

  if (loading) {
    return (
      <div className="loading-screen">
        <SecureImg src={STAFF_LOGO} alt="Mobilistan" className="logo" />
        <p>Loading...</p>
      </div>
    );
  }

  if (isProductPage) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AlSamiProductPage
          slug={productSlug}
          settings={settings}
          onBack={() => window.history.back()}
        />
      </Suspense>
    );
  }

  // Customer-facing self-service portal. Handles its own auth via CNIC +
  // password (see CustomerPortal.js). Does not use admin AppLayout chrome.
  if (isPortalRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <CustomerPortal />
      </Suspense>
    );
  }

  if (isAdminRoute) {
    if (!user) return <Login onLogin={() => {}} />;
    // Gate post-login admin route until permissions are read & applied.
    // Without this gate, AdminPanel briefly renders with stale state (default
    // employeePerms === null = unrestricted) and flashes the full nav.
    if (permissionsLoading) {
      return (
        <div className="ap-perms-loading">
          <div className="ap-perms-spinner" aria-label="Loading" />
        </div>
      );
    }
    if (mustChangePw && user) {
      return (
        <ChangePasswordRequired
          user={user}
          employeeId={agentEmployee?.id || user.uid}
          onDone={() => setMustChangePw(false)}
          onLogout={handleLogout}
        />
      );
    }
    // Fix 6: Per-user access date window gate. Applies to staff/employee logins
    // (not the customer portal — that route returned earlier). Blocks the
    // dashboard before accessFromDate and after accessToDate. Empty fields = no
    // limit. This runs before any dashboard mounting; it contains no hooks.
    {
      const todayStr = new Date().toLocaleDateString('en-CA'); // 'YYYY-MM-DD' (local)
      const fromDate = accessWindow?.from || '';
      const toDate   = accessWindow?.to   || '';
      if (fromDate && todayStr < fromDate) {
        return (
          <div className="ap-perms-loading" style={{ flexDirection: 'column', gap: 20, padding: 24, textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 20, color: '#111827' }}>Access not yet active</h2>
            <p style={{ margin: 0, color: '#6b7280', maxWidth: 420 }}>
              Your account becomes active on {fromDate}.
            </p>
            <button type="button" onClick={handleLogout}
              style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#111827', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              Sign out
            </button>
          </div>
        );
      }
      if (toDate && todayStr > toDate) {
        return (
          <div className="ap-perms-loading" style={{ flexDirection: 'column', gap: 20, padding: 24, textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 20, color: '#111827' }}>Access expired</h2>
            <p style={{ margin: 0, color: '#6b7280', maxWidth: 420 }}>
              Your account access ended on {toDate}. Contact admin.
            </p>
            <button type="button" onClick={handleLogout}
              style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#111827', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              Sign out
            </button>
          </div>
        );
      }
    }
    if (userRole === 'cityManager' && agentEmployee) {
      return (
        <AppLayout employee={agentEmployee} userRole="cityManager" permissions={employeePerms} onLogout={handleLogout} activeSection="dashboard" onSectionChange={() => {}}>
          <InactivityLogout onLogout={handleLogout} />
          <Suspense fallback={<PageLoader />}>
            <CityManagerDashboard employee={agentEmployee} onLogout={handleLogout} />
          </Suspense>
        </AppLayout>
      );
    }
    if (userRole === 'branchManager' && agentEmployee) return (
      <AppLayout employee={agentEmployee} userRole="branchManager" permissions={employeePerms} onLogout={handleLogout} activeSection="dashboard" onSectionChange={() => {}}>
        <InactivityLogout onLogout={handleLogout} />
        <Suspense fallback={<PageLoader />}>
          <BranchManagerDashboard employee={agentEmployee} />
        </Suspense>
      </AppLayout>
    );
    if (userRole === 'fieldCoordinator' && agentEmployee) return (
      <Suspense fallback={<PageLoader />}>
        <AppLayout employee={agentEmployee} userRole="fieldCoordinator" permissions={employeePerms} onLogout={handleLogout} activeSection="dashboard" onSectionChange={() => {}}>
          <FieldCoordinatorDashboard employee={agentEmployee} />
        </AppLayout>
      </Suspense>
    );
    if (userRole === 'salesman' && agentEmployee) return (
      <AppLayout employee={agentEmployee} userRole="salesman" permissions={employeePerms} onLogout={handleLogout} activeSection="dashboard" onSectionChange={() => {}}>
        <InactivityLogout onLogout={handleLogout} />
        <Suspense fallback={<PageLoader />}>
          <SalesmanDashboard employee={agentEmployee} />
        </Suspense>
      </AppLayout>
    );
    if (userRole === 'legalOfficer' && agentEmployee) return (
      <Suspense fallback={<PageLoader />}>
        <AppLayout employee={agentEmployee} userRole="legalOfficer" permissions={employeePerms} onLogout={handleLogout} activeSection="dashboard" onSectionChange={() => {}}>
          <LegalOfficerDashboard employee={agentEmployee} />
        </AppLayout>
      </Suspense>
    );
    if ((userRole === 'agent' || userRole === 'recovery') && agentEmployee) {
      const DashboardComp = userRole === 'recovery' ? RecoveryDashboard : AgentDashboard;
      return (
        <AppLayout employee={agentEmployee} userRole={userRole} permissions={employeePerms} onLogout={handleLogout} activeSection="dashboard" onSectionChange={() => {}}>
          <InactivityLogout onLogout={handleLogout} />
          <Suspense fallback={<PageLoader />}>
            <DashboardComp employee={agentEmployee} />
          </Suspense>
        </AppLayout>
      );
    }
    // Super-admin → hardcoded "Samiullah Sadiq" (no employee record exists for the
    // owner account). Employee with admin role → name from /employees/{uid}/name.
    // Fallback for any other case → email prefix or "Admin".
    const adminName = isSuperAdmin
      ? 'Samiullah Sadiq'
      : (employeeName || user?.displayName || (user?.email ? user.email.split('@')[0] : 'Admin'));
    return (
      <div className="app">
        <main>
          <Suspense fallback={<PageLoader />}>
            <AdminPanel adminName={adminName} onLogout={handleLogout} permissions={isSuperAdmin ? null : employeePerms} isSuperAdmin={isSuperAdmin} />
          </Suspense>
        </main>
      </div>
    );
  }

  // Sale is active when enabled and end date hasn't passed
  const saleActive = sale?.enabled && sale?.endDate && new Date(sale.endDate) >= new Date();

  // ── public site ──
  return (
    <ErrorBoundary>
    <SettingsProvider value={settings}>
    <Suspense fallback={<PageLoader />}>
      <div className="app">

        {/* Announcement bar */}
        {ann?.enabled && ann?.text && (
          ann?.link
            ? (
              <SecureLink
                href={ann.link}
                className="site-announce"
                style={{ background: ann.bgColor || undefined, color: ann.textColor || undefined, display: 'block', textDecoration: 'none' }}
              >
                {ann.text}
              </SecureLink>
            )
            : (
              <div
                className="site-announce"
                style={{ background: ann.bgColor || undefined, color: ann.textColor || undefined }}
              >
                {ann.text}
              </div>
            )
        )}

        {/* Drawer */}
        {drawerOpen && <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />}
        <div className={`nav-drawer${drawerOpen ? ' open' : ''}`}>
          <div className="drawer-header">
            <span className="drawer-title">Menu</span>
            <button className="drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
          </div>
          {categories.length > 0 && (
            <>
              <p className="drawer-section-label">Shop by Category</p>
              <nav className="drawer-nav">
                {categories.map(cat => (
                  <button
                    key={cat.id || cat.name}
                    className="drawer-nav-btn drawer-nav-btn--cat"
                    onClick={() => goToCategory(cat)}
                  >
                    {cat.icon
                      ? <SecureImg src={cat.icon} alt={cat.name} className="drawer-cat-icon" />
                      : <span className="drawer-cat-icon drawer-cat-icon--fallback">
                          {(cat.name || '').charAt(0).toUpperCase()}
                        </span>
                    }
                    <span className="drawer-cat-name">{cat.name}</span>
                  </button>
                ))}
              </nav>
            </>
          )}
          <p className="drawer-section-label">More</p>
          <nav className="drawer-nav">
            <button className="drawer-nav-btn" onClick={() => goTo('track')}>Track Order</button>
            <button className="drawer-nav-btn" onClick={() => goTo('contact')}>Contact Us</button>
          </nav>
        </div>

        <header className="header">
          {/* Row 1: Logo left · Search icon right */}
          <div className="header-row1">
            <SecureImg
              src={general.logoUrl || '/logo.png'}
              alt={general.siteName || 'Al Sami Electronics'}
              className="logo"
              onClick={() => goTo('home')}
              style={{ cursor: 'pointer' }}
              loading="eager"
              fetchPriority="high"
            />
            <button
              className="search-icon-btn"
              onClick={() => { setSearchOpen(o => !o); if (searchOpen) setSearchQuery(''); }}
              aria-label={searchOpen ? 'Close search' : 'Search'}
            >
              {searchOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </button>
          </div>
          {/* Row 2: Hamburger left · Cash/Installment center */}
          <div className="header-row2">
            <button className="hamburger-btn" onClick={() => setDrawerOpen(o => !o)} aria-label="Menu">☰</button>
            {activePage === 'home' && !isProductPage && (
              <div className="header-filter-group">
                <button
                  className={`home-filter-btn${homeFilter === 'cash' ? ' active' : ''}`}
                  onClick={() => setHomeFilter('cash')}
                >
                  <span className="filter-btn-short">Cash</span>
                  <span className="filter-btn-long">Buy on Cash</span>
                </button>
                <button
                  className={`home-filter-btn${homeFilter === 'installment' ? ' active' : ''}`}
                  onClick={() => setHomeFilter('installment')}
                >
                  <span className="filter-btn-short">Installment</span>
                  <span className="filter-btn-long">Buy on Installment</span>
                </button>
              </div>
            )}
          </div>
          {/* Expanding search bar */}
          {searchOpen && (
            <div className="header-search-bar">
              <input
                className="header-search-input"
                type="search"
                placeholder="Search products…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          )}
        </header>

        <main>
          {activePage === 'home' && (
            <div>
              {categories.length > 0 && (
                <section className="cat-strip-wrap" aria-label="Shop by category">
                  <div className="cat-strip">
                    {categories.map(cat => (
                      <button
                        key={cat.id || cat.name}
                        type="button"
                        className="cat-tile"
                        onClick={() => goToCategory(cat)}
                      >
                        <span className="cat-tile-icon">
                          {cat.icon
                            ? <SecureImg src={cat.icon} alt={cat.name} />
                            : <span className="cat-tile-fallback">
                                {(cat.name || '').charAt(0).toUpperCase()}
                              </span>}
                        </span>
                        <span className="cat-tile-name">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
              {banners.length > 0 && <BannerCarousel banners={banners} />}
              <section className="section home-products-section">
                <Products type={homeFilter} searchQuery={searchQuery} saleSettings={sale} />
              </section>
            </div>
          )}
          {activePage === 'cash' && (
            <section className="section">
              <Products type="cash" initialCategory={activeCategory?.name} saleSettings={sale} />
            </section>
          )}
          {activePage === 'installment' && (
            <section className="section">
              <Products type="installment" initialCategory={activeCategory?.name} saleSettings={sale} />
            </section>
          )}
          {activePage === 'category' && activeCategory && (
            <section className="section">
              <CategoryPage
                categoryName={activeCategory.name}
                onBack={() => goTo('home')}
                onBuyCash={(product) => setCatOrder({ product, type: 'cash' })}
                onBuyInstallment={(product) => setCatOrder({ product, type: 'installment' })}
              />
              {catOrder && (
                <OrderModal
                  product={catOrder.product}
                  type={catOrder.type}
                  onClose={() => setCatOrder(null)}
                />
              )}
            </section>
          )}
          {activePage === 'track'   && <section className="section"><TrackOrder /></section>}
          {activePage === 'contact' && <section className="section"><ContactUs general={general} whatsapp={whatsapp} /></section>}
        </main>

        <footer className="footer">
          <div className="footer-content">
            <div className="footer-brand">
              <SecureImg src={general.logoUrl || '/logo.png'} alt={general.siteName || 'Al Sami Electronics'} className="logo" loading="lazy" decoding="async" />
              <p>Your trusted destination for premium electronics in {general.address ? general.address.split(',')[0] : 'Quetta'}. Quality products, honest prices, flexible installment plans.</p>
              {(social.facebook || social.instagram || social.tiktok || social.youtube || social.twitter) && (
                <div className="footer-social">
                  {social.facebook  && <SecureLink href={social.facebook}  target="_blank" rel="noreferrer">Facebook</SecureLink>}
                  {social.instagram && <SecureLink href={social.instagram} target="_blank" rel="noreferrer">Instagram</SecureLink>}
                  {social.tiktok    && <SecureLink href={social.tiktok}    target="_blank" rel="noreferrer">TikTok</SecureLink>}
                  {social.youtube   && <SecureLink href={social.youtube}   target="_blank" rel="noreferrer">YouTube</SecureLink>}
                  {social.twitter   && <SecureLink href={social.twitter}   target="_blank" rel="noreferrer">Twitter</SecureLink>}
                </div>
              )}
            </div>
            <div className="footer-col">
              <h4>Shop</h4>
              <button type="button" onClick={() => goTo('cash')}>Buy on Cash</button>
              <button type="button" onClick={() => goTo('installment')}>Buy on Installment</button>
              <button type="button" onClick={() => goTo('track')}>Track Order</button>
            </div>
            <div className="footer-col">
              <h4>Contact</h4>
              <p>{general.address || 'Quetta, Balochistan'}</p>
              <p>{general.phone   || '0317-8600078'}</p>
              {general.email && <p>{general.email}</p>}
              <button type="button" onClick={() => goTo('contact')}>Contact Us</button>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} {general.siteName || 'Al Sami Electronics'}. All rights reserved.</p>
            <p>Quetta, Pakistan</p>
          </div>
        </footer>

        {/* WhatsApp FAB */}
        <WaFab settings={whatsapp} />

        {/* Sale banner (shown below header when active) */}
        {saleActive && sale.bannerUrl && (
          <div className="sale-banner-strip">
            <SecureImg src={sale.bannerUrl} alt={sale.name || 'Sale'} className="sale-banner-img" loading="eager" fetchPriority="high" />
          </div>
        )}

        {/* Popup */}
        {showPopup && (
          <SitePopup settings={popup} onClose={() => setShowPopup(false)} />
        )}

      </div>
    </Suspense>
    </SettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
